import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Hash, ListChecks, Sparkles, Flame, Sun, CloudSun, Moon, Quote, Camera, Clock, TrendingUp, Copy, Check } from "lucide-react";
import { ViralScoreBadge } from "./ViralScore";
import { toast } from "sonner";

const TIME_ICONS = {
  morning: <Sun className="h-3.5 w-3.5 text-[#FFD66B]" />,
  afternoon: <CloudSun className="h-3.5 w-3.5 text-[#FF9F66]" />,
  evening: <Moon className="h-3.5 w-3.5 text-[#A78BFA]" />,
};

export const CreatorBrief = ({ itinerary }) => {
  const top5 = itinerary?.top_viral_spots || [];
  const checklist = itinerary?.content_checklist || [];
  const hashtags = itinerary?.suggested_hashtags || [];
  const tip = itinerary?.final_creator_tip;
  const days = itinerary?.days || [];

  if (!top5.length && !checklist.length && !hashtags.length && !tip && !days.length) return null;

  return (
    <section className="px-5 sm:px-8 py-12 sm:py-16">
      <div className="mx-auto max-w-6xl space-y-12">
        <header>
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FE0979] mb-2 inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Creator brief
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
            Your AI-curated <span className="text-gradient">shoot plan</span>.
          </h2>
        </header>

        {/* DAY-LEVEL CREATOR PLAN */}
        {days.length > 0 && (
          <div className="space-y-4" data-testid="brief-days">
            {days.map((d, i) => (
              <DayBriefCard key={i} day={d} index={i} itineraryId={itinerary.id} />
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* TOP 5 VIRAL SPOTS */}
          {top5.length > 0 && (
            <div className="glass rounded-3xl p-5 sm:p-6 lg:col-span-2" data-testid="brief-top5">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#00F2FE] mb-4 flex items-center gap-1.5">
                <Flame className="h-3.5 w-3.5" /> Top 5 viral spots
              </div>
              <ol className="space-y-3">
                {top5.map((s, i) => (
                  <li key={s.id || i}>
                    <Link
                      to={`/spot/${itinerary.id}/${s.id}`}
                      data-testid={`brief-top5-${s.id || i}`}
                      className="group flex items-center gap-3 rounded-2xl p-2 -mx-2 hover:bg-white/5 transition"
                    >
                      <span className="font-display font-black text-2xl text-zinc-500 w-6 shrink-0 text-center">{i + 1}</span>
                      <img src={s.image} alt={s.name} className="h-12 w-12 rounded-xl object-cover ring-1 ring-white/10 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold text-sm leading-tight truncate">{s.name}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 truncate">
                          {s.city}{s.country ? `, ${s.country}` : ""}
                        </div>
                      </div>
                      <ViralScoreBadge breakdown={s.viral_breakdown} fallbackScore={s.viral_score} testId={`brief-top5-badge-${i}`} />
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* CONTENT CHECKLIST */}
          {checklist.length > 0 && (
            <div className="glass rounded-3xl p-5 sm:p-6" data-testid="brief-checklist">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#FE0979] mb-4 flex items-center gap-1.5">
                <ListChecks className="h-3.5 w-3.5" /> Content checklist
              </div>
              <ChecklistList items={checklist} />
            </div>
          )}
        </div>

        {/* HASHTAGS */}
        {hashtags.length > 0 && (
          <div className="glass rounded-3xl p-5 sm:p-6" data-testid="brief-hashtags">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#00F2FE] mb-3 flex items-center gap-1.5">
              <Hash className="h-3.5 w-3.5" /> Suggested hashtags
            </div>
            <HashtagBank tags={hashtags} />
          </div>
        )}

        {/* FINAL CREATOR TIP */}
        {tip && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
            data-testid="brief-final-tip"
            className="glass-strong rounded-3xl p-6 sm:p-8 relative overflow-hidden"
          >
            <div className="absolute -top-24 -left-12 h-[280px] w-[280px] rounded-full bg-[#FE0979]/15 blur-3xl" />
            <div className="absolute -bottom-24 -right-12 h-[280px] w-[280px] rounded-full bg-[#00F2FE]/12 blur-3xl" />
            <div className="relative">
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FE0979] mb-2 inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> Final creator tip
              </div>
              <p className="font-display font-bold text-xl sm:text-2xl leading-snug text-shadow-soft">{tip}</p>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

const DayBriefCard = ({ day, index, itineraryId }) => {
  const slots = [
    { key: "morning_spot", time: "morning" },
    { key: "afternoon_spot", time: "afternoon" },
    { key: "evening_spot", time: "evening" },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      data-testid={`brief-day-${day.day || index + 1}`}
      className="glass rounded-3xl p-5 sm:p-6"
    >
      <div className="flex items-baseline gap-3 mb-4">
        <div className="font-display font-black text-3xl text-gradient leading-none">D{day.day || index + 1}</div>
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400">Day {day.day || index + 1}</div>
          {day.destination && <div className="font-display font-bold text-base truncate">{day.destination}</div>}
        </div>
        {day.best_time_to_shoot && (
          <span className="ml-auto glass rounded-full px-2.5 py-1 inline-flex items-center gap-1.5 shrink-0">
            <Clock className="h-3 w-3 text-[#00F2FE]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] truncate max-w-[180px]">{day.best_time_to_shoot}</span>
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {slots.map(({ key, time }) => {
          const s = day[key];
          if (!s) return <div key={key} className="rounded-2xl bg-white/5 border border-white/10 p-3 text-xs text-zinc-500 inline-flex items-center gap-1.5">{TIME_ICONS[time]} Free time</div>;
          return (
            <Link
              key={key}
              to={`/spot/${itineraryId}/${s.id}`}
              data-testid={`brief-day-${day.day}-${time}`}
              className="rounded-2xl bg-white/5 border border-white/10 p-3 hover:border-white/25 transition group block"
            >
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400 mb-1.5">
                {TIME_ICONS[time]} {time}
              </div>
              <div className="font-display font-bold text-sm leading-tight">{s.name}</div>
              {s.city && <div className="text-[10px] text-zinc-500 truncate">{s.city}</div>}
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        {day.reel_idea && <BriefField icon={<Camera className="h-3 w-3 text-[#00F2FE]" />} label="Reel idea">{day.reel_idea}</BriefField>}
        {day.instagram_caption && <BriefField icon={<Quote className="h-3 w-3 text-[#FE0979]" />} label="IG caption"><span className="italic">"{day.instagram_caption}"</span></BriefField>}
        {day.why_it_performs && <BriefField icon={<TrendingUp className="h-3 w-3 text-[#FE0979]" />} label="Why it'll perform">{day.why_it_performs}</BriefField>}
      </div>
    </motion.div>
  );
};

const BriefField = ({ icon, label, children }) => (
  <div>
    <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1 flex items-center gap-1.5">
      {icon} {label}
    </div>
    <div className="text-zinc-200 font-body text-xs leading-relaxed">{children}</div>
  </div>
);

const ChecklistList = ({ items }) => {
  const [done, setDone] = useState(() => new Set());
  const toggle = (i) => {
    const next = new Set(done);
    next.has(i) ? next.delete(i) : next.add(i);
    setDone(next);
  };
  return (
    <ul className="space-y-2">
      {items.map((it, i) => {
        const checked = done.has(i);
        return (
          <li key={i}>
            <button
              type="button"
              onClick={() => toggle(i)}
              data-testid={`checklist-item-${i}`}
              className="w-full text-left flex items-start gap-2.5 group"
            >
              <span className={`mt-0.5 h-4 w-4 rounded-md border flex items-center justify-center shrink-0 transition ${checked ? "bg-[#FE0979] border-[#FE0979]" : "border-white/25 group-hover:border-white/50"}`}>
                {checked && <Check className="h-3 w-3 text-black" />}
              </span>
              <span className={`text-xs sm:text-sm font-body leading-snug transition ${checked ? "text-zinc-500 line-through" : "text-zinc-200"}`}>
                {it}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const HashtagBank = ({ tags }) => {
  const all = useMemo(() => tags.join(" "), [tags]);
  const [copied, setCopied] = useState(false);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(all);
      setCopied(true);
      toast.success("All hashtags copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy");
    }
  };

  const copyOne = async (t) => {
    try {
      await navigator.clipboard.writeText(t);
      toast.success(`Copied ${t}`);
    } catch (_) {}
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {tags.map((t, i) => (
          <button
            key={i}
            type="button"
            onClick={() => copyOne(t)}
            data-testid={`hashtag-${i}`}
            className="text-xs font-medium text-zinc-200 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-2.5 py-1 transition inline-flex items-center gap-1"
            title="Copy hashtag"
          >
            {t}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={copyAll}
        data-testid="hashtags-copy-all"
        className="text-[10px] font-bold uppercase tracking-[0.2em] glass rounded-full px-3 py-1.5 hover:bg-white/10 transition inline-flex items-center gap-1.5"
      >
        {copied ? <Check className="h-3 w-3 text-[#00F2FE]" /> : <Copy className="h-3 w-3" />}
        {copied ? "Copied all" : "Copy all"}
      </button>
    </div>
  );
};
